import { useCallback, useEffect, useRef } from 'react';

import { useSubscribeAuth } from '@/utils/websocket/hooks';
import { TrustlyStatusUpdateMessage } from '@/utils/websocket/types';

import { TrustlyStatus, fetchTrustlyStatus } from '../utils/trustly-status';

type UseTrustlyStatusOptions = {
    onSuccess: (status: TrustlyStatus) => void;
    onError: (error: unknown) => void;
    timeoutMs?: number;
    isWebsocketActive?: boolean;
};

type UseTrustlyStatusReturn = {
    startTimerForCheckStatusApiAsFallback: (userWalletId: string) => void;
    cleanup: () => void;
};

/**
 * Hook for handling Trustly status updates via websocket with HTTP API fallback
 * @param options Configuration options for the hook
 * @returns An object with utility functions and state for handling Trustly status
 */
export const useTrustlyStatus = ({
    onSuccess,
    onError,
    timeoutMs = 30000,
    isWebsocketActive = true,
}: UseTrustlyStatusOptions): UseTrustlyStatusReturn => {
    const websocketStatusReceivedRef = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Handle status updates from any source (websocket or HTTP)
    const handleStatusUpdate = useCallback(
        (status: TrustlyStatus) => {
            // Clear any pending timeout when we get a response
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            onSuccess(status);
        },
        [onSuccess]
    );

    // WebSocket message handler
    const handleWebSocketMessage = useCallback(
        (message: TrustlyStatusUpdateMessage) => {
            websocketStatusReceivedRef.current = true;
            handleStatusUpdate(message.status);
        },
        [handleStatusUpdate]
    );

    // Subscribe to Trustly status updates via websocket
    useSubscribeAuth<'trustlyStatusUpdate'>('trustly_status_update', handleWebSocketMessage, isWebsocketActive);

    // Setup the HTTP API fallback mechanism with a timer
    const startTimerForCheckStatusApiAsFallback = useCallback(
        (userWalletId: string) => {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Set up HTTP API fallback if websocket doesn't receive response within timeout period
            timeoutRef.current = setTimeout(() => {
                if (!websocketStatusReceivedRef.current) {
                    fetchTrustlyStatus({ userWalletId })
                        .then(response => {
                            handleStatusUpdate(response.data.status);
                        })
                        .catch(error => {
                            onError(error);
                        });
                }
            }, timeoutMs);
        },
        [handleStatusUpdate, timeoutMs, onError]
    );

    // Cleanup function to clear the timeout and reset flags
    const cleanup = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        // Reset the websocket status flag but keep the processed flag
        websocketStatusReceivedRef.current = false;
    }, []);

    // Ensure cleanup on unmount
    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    return {
        startTimerForCheckStatusApiAsFallback,
        cleanup,
    };
};
